import React, { useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useAlbumLibrary } from '@/features/album/hooks/useAlbumLibrary'
import { useAlbumDetail } from '@/features/album/hooks/useAlbumDetail'
import { AlbumPhoto } from '@/features/album/types'
import { GlassPanel, monoLabelSx, STUDIO_ACCENT } from '../shared'

export interface StudioFirstRunOverlayProps {
  /** Fires once an album is chosen and confirmed — parent hangs `photos` in the room's Classic slots, links `albumId`, and dismisses. */
  onConfirm: (albumId: string, photos: AlbumPhoto[]) => void
  onSkip: () => void
}

const Veil = styled(Box)({
  position: 'fixed',
  inset: 0,
  zIndex: 80,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(6,5,10,0.4)',
  backdropFilter: 'blur(3px)',
  WebkitBackdropFilter: 'blur(3px)',
})

const Panel = styled(GlassPanel)({
  width: 320,
  maxWidth: 'calc(100vw - 40px)',
  padding: '22px 24px',
  background: 'rgba(23,22,29,0.97)',
})

const Eyebrow = styled(Box)({ ...monoLabelSx, fontSize: 8, color: STUDIO_ACCENT.cyan, marginBottom: 6 })
const Title = styled(Box)({ fontSize: 16, fontWeight: 700, letterSpacing: '-0.015em', marginBottom: 16 })

const ColLabel = styled(Box)({ ...monoLabelSx, marginBottom: 8 })

const AlbumList = styled(Box)({ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 220, overflowY: 'auto' })

const AlbumRow = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(({ $on }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  padding: '6px 8px',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 11.5,
  fontWeight: 620,
  color: $on ? STUDIO_ACCENT.violet : 'var(--ds-night-ink-muted)',
  background: $on ? 'rgba(190,157,253,0.12)' : 'transparent',
  '&:hover': { color: $on ? STUDIO_ACCENT.violet : 'var(--ds-night-ink)' },
}))

const AlbumThumb = styled(Box)({
  width: 26,
  height: 26,
  borderRadius: 6,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  flexShrink: 0,
  background: 'rgba(244,243,246,0.08)',
})

const AlbumMeta = styled(Box)({ overflow: 'hidden' })
const AlbumName = styled('div')({ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })
const AlbumCount = styled('div')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8,
  color: 'var(--ds-night-ink-subtle)',
  marginTop: 1,
})

const EmptyNote = styled(Box)({ fontSize: 11, lineHeight: 1.6, color: 'var(--ds-night-ink-subtle)' })

const Foot = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: 18,
  paddingTop: 14,
  borderTop: '1px solid var(--ds-night-border)',
})

const SkipLink = styled('button')({
  all: 'unset',
  cursor: 'pointer',
  fontSize: 11,
  color: 'var(--ds-night-ink-subtle)',
  textDecoration: 'underline',
  '&:hover': { color: 'var(--ds-night-ink-muted)' },
})

const GoButton = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  height: 32,
  padding: '0 16px',
  borderRadius: 8,
  background: 'var(--ds-night-ink)',
  color: '#0a0912',
  fontSize: 11.5,
  fontWeight: 650,
  cursor: 'pointer',
  '&:disabled': { opacity: 0.45, cursor: 'default' },
})

/**
 * First-run overlay inside Studio3D itself. Shown only while
 * `useStudio3DData().isNewGallery` is true (a just-stub-created gallery,
 * nothing saved yet). Asks "which album" live, over the already-mounted
 * canvas, instead of gating navigation on a pre-flight modal or wizard —
 * dismissable either way. Used to also offer a starting scene
 * (corridor/loft/pavilion); that column is gone for now since Classic is
 * the fixed launch default and Studio's scenes are reachable only from
 * `RoomInspector`, and only for galleries already in Studio mode — see
 * task #17.
 */
export function StudioFirstRunOverlay({ onConfirm, onSkip }: StudioFirstRunOverlayProps) {
  const { albums, loading: albumsLoading } = useAlbumLibrary()
  const [selectedAlbumId, setSelectedAlbumId] = useState<string | null>(null)

  // Only picks a default once the album list actually resolves — an
  // effect-free derivation (not useEffect+setState) so there's no render
  // where a stale/empty selection briefly renders as "chosen".
  const activeAlbumId = selectedAlbumId ?? (albums.length > 0 ? albums[0].id : null)
  const activeAlbum = useMemo(() => albums.find((a) => a.id === activeAlbumId) || null, [albums, activeAlbumId])

  const { album: albumDetail, loading: photosLoading } = useAlbumDetail(activeAlbumId || undefined)

  const canConfirm = !!activeAlbum && !photosLoading && (albumDetail?.photos.length ?? 0) > 0

  return (
    <Veil>
      <Panel>
        <Eyebrow>◈ First-run · lives inside Studio</Eyebrow>
        <Title>Set this room up</Title>
        <ColLabel>Album</ColLabel>
        {albumsLoading ? (
          <EmptyNote>Loading your albums…</EmptyNote>
        ) : albums.length === 0 ? (
          <EmptyNote>No albums yet — skip for now and hang prints once you've created one.</EmptyNote>
        ) : (
          <AlbumList>
            {albums.map((a) => (
              <AlbumRow key={a.id} type='button' $on={a.id === activeAlbumId} onClick={() => setSelectedAlbumId(a.id)}>
                <AlbumThumb sx={a.coverUrl ? { backgroundImage: `url(${a.coverUrl})` } : undefined} />
                <AlbumMeta>
                  <AlbumName>{a.title}</AlbumName>
                  <AlbumCount>{a.photoCount} photos</AlbumCount>
                </AlbumMeta>
              </AlbumRow>
            ))}
          </AlbumList>
        )}
        <Foot>
          <SkipLink type='button' onClick={onSkip}>
            Skip, use defaults
          </SkipLink>
          <GoButton
            type='button'
            disabled={!canConfirm}
            onClick={() => activeAlbum && albumDetail && onConfirm(activeAlbum.id, albumDetail.photos)}
          >
            {photosLoading && activeAlbum ? 'Loading…' : 'Hang prints →'}
          </GoButton>
        </Foot>
      </Panel>
    </Veil>
  )
}
